import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import AppFooter from "@/components/layout/AppFooter";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";

const LOGO_URL = "/smartdienstplan-logo.svg";

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password || !confirmPassword) {
      toast.error("Bitte fülle alle Felder aus");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Die Passwörter stimmen nicht überein");
      return;
    }
    
    if (password.length < 6) {
      toast.error("Das Passwort muss mindestens 6 Zeichen lang sein");
      return;
    }
    
    setLoading(true);
    try {
      const result = await register(name, email, password);
      toast.success(result?.message || "Registrierung erfolgreich. Bitte bestätige deine E-Mail-Adresse.");
      navigate("/login");
    } catch (error) {
      console.error("Register error:", error);
      toast.error(error.response?.data?.detail || "Registrierung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col" data-testid="register-page">
      <div className="auth-container flex-1">
        {/* Image Side - Dark with Logo */}
        <div className="auth-image-side bg-[#212529]">
          <div className="absolute inset-0 bg-[#212529] flex items-center justify-center">
            <div className="text-center p-8">
              <img src={LOGO_URL} alt="smartdienstplan" className="w-32 h-32 mx-auto mb-8" />
              <h2 className="font-heading text-3xl font-bold text-white mb-4">Starte jetzt!</h2>
              <p className="text-gray-400 text-lg max-w-md">
                Erstelle dein Konto und beginne sofort mit der Planung deiner Dienstpläne.
              </p>
            </div>
          </div>
        </div>
        
        {/* Form Side - Light */}
        <div className="auth-form-side bg-[#f8fafc]">
          <div className="w-full max-w-md">
            <Link to="/" className="flex items-center gap-3 mb-8">
              <div className="w-8 h-8 bg-[#212529] rounded-lg flex items-center justify-center p-1">
                <img src={LOGO_URL} alt="smartdienstplan" className="w-full h-full" />
              </div>
              <span className="font-heading font-semibold text-xl text-[#212529]">smartdienstplan</span>
            </Link>
            
            <Card className="border-gray-200 bg-white shadow-lg">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="font-heading text-2xl text-[#212529]">Konto erstellen</CardTitle>
                <CardDescription className="text-gray-500">
                  Registriere dich als Dienstplan-Besitzer
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#212529]">Name</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Dein Name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="h-11 border-gray-300 focus:border-[#17c671] focus:ring-[#17c671]/20"
                      data-testid="register-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#212529]">E-Mail</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="deine@email.de"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-11 border-gray-300 focus:border-[#17c671] focus:ring-[#17c671]/20"
                      data-testid="register-email-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-[#212529]">Passwort</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-11 pr-10 border-gray-300 focus:border-[#17c671] focus:ring-[#17c671]/20"
                        data-testid="register-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-[#212529]">Passwort bestätigen</Label>
                    <Input
                      id="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="h-11 border-gray-300 focus:border-[#17c671] focus:ring-[#17c671]/20"
                      data-testid="register-confirm-password-input"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-[#17c671] hover:bg-[#14a85d] text-white font-semibold btn-active"
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Registrieren...
                      </>
                    ) : (
                      "Konto erstellen"
                    )}
                  </Button>
                </form>
                
                <div className="mt-6 text-center text-sm text-gray-500">
                  Bereits ein Konto?{" "}
                  <Link to="/login" className="text-[#17c671] hover:text-[#14a85d] font-medium">
                    Jetzt anmelden
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <AppFooter />
    </div>
  );
};

export default RegisterPage;
